import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, ArrowRightLeft, Check, X, Play, Pause, History, Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmt = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const StandingInstructionsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistory, setShowHistory] = useState(null);
  const [history, setHistory] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [clientLoans, setClientLoans] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [form, setForm] = useState({
    name: '', client_id: '', from_account_id: '', to_account_id: '', amount: '',
    frequency: 'monthly', day_of_month: '1', start_date: '', end_date: '',
    instruction_type: 'transfer', priority: 'medium',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/standing-instructions'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const searchClients = async (q) => {
    setClientSearch(q);
    if (q.length < 2) { setClientResults([]); return; }
    try { 
      const res = await api.get(`/clients/lookup?q=${encodeURIComponent(q)}`);
      // Handle both single match and multiple matches
      if (res.data?.match === 'single' && res.data?.client) {
        setClientResults([res.data.client]);
      } else if (res.data?.match === 'multiple' && res.data?.clients) {
        setClientResults(res.data.clients);
      } else if (Array.isArray(res.data)) {
        setClientResults(res.data);
      } else {
        setClientResults([]);
      }
    }
    catch { setClientResults([]); }
  };

  const selectClient = async (client) => {
    setForm(f => ({ ...f, client_id: client.id, from_account_id: '', to_account_id: '' }));
    setClientSearch(`${client.first_name} ${client.last_name}`);
    setClientResults([]);
    try {
      const [accRes, loanRes] = await Promise.all([
        api.get(`/savings-accounts?client_id=${client.id}`).catch(() => ({ data: { accounts: [] } })),
        api.get(`/loans?client_id=${client.id}`).catch(() => ({ data: [] })),
      ]);
      setClientAccounts(accRes.data?.accounts || accRes.data || []);
      setClientLoans(Array.isArray(loanRes.data) ? loanRes.data.filter(l => ['active', 'disbursed'].includes(l.status)) : []);
    } catch { setClientAccounts([]); setClientLoans([]); }
  };

  const handleSubmit = async () => {
    if (!form.client_id || !form.from_account_id || !form.amount) return toast.error('Client, source account, and amount are required');
    try {
      await api.post('/standing-instructions', { ...form, amount: parseFloat(form.amount), day_of_month: parseInt(form.day_of_month) || 1 });
      toast.success('Standing instruction created — pending approval');
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleAction = async (id, action) => {
    try {
      await api.post(`/standing-instructions/${id}/${action}`);
      toast.success(`Instruction ${action}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this standing instruction?')) return;
    try { await api.delete(`/standing-instructions/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const viewHistory = async (id) => {
    try { const res = await api.get(`/standing-instructions/${id}/history`); setHistory(res.data); setShowHistory(id); }
    catch { toast.error('Failed to load history'); }
  };

  const handleExecuteBatch = async () => {
    if (!window.confirm('Execute all due standing instructions now?')) return;
    try {
      const res = await api.post('/standing-instructions/execute-batch');
      toast.success(`Executed: ${res.data.executed} success, ${res.data.failed} failed, ${res.data.skipped} skipped`);
      load();
    } catch (e) { toast.error('Failed to execute batch'); }
  };

  const resetForm = () => {
    setShowModal(false); setClientSearch(''); setClientResults([]); setClientAccounts([]); setClientLoans([]);
    setForm({ name: '', client_id: '', from_account_id: '', to_account_id: '', amount: '', frequency: 'monthly', day_of_month: '1', start_date: '', end_date: '', instruction_type: 'transfer', priority: 'medium' });
  };

  const statusStyles = {
    active: 'bg-emerald-50 text-emerald-700', pending_approval: 'bg-amber-50 text-amber-700',
    paused: 'bg-blue-50 text-blue-700', rejected: 'bg-red-50 text-red-700',
    completed: 'bg-gray-100 text-gray-500',
  };

  return (
    <AdminSubPage title="Standing Instructions" breadcrumb={['Admin', 'Organization', 'Standing Instructions']} backPath="/dashboard/admin/organization">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-500">Automated recurring transfers and loan repayments. Created per client, approved by management (maker-checker).</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExecuteBatch} data-testid="execute-batch-btn"><Play className="w-4 h-4 mr-1" />Execute Due</Button>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#1f1f1f] text-white" data-testid="add-si-btn"><Plus className="w-4 h-4 mr-1" />New Instruction</Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200" data-testid="standing-instructions-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Client</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">From / To</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Amount</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Frequency</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Next Exec</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Runs</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={9} className="text-center py-8 text-gray-400"><ArrowRightLeft className="w-8 h-8 mx-auto mb-2 text-gray-300" />No standing instructions.</td></tr> :
            items.map(si => (
              <tr key={si.id} className="border-b hover:bg-gray-50" data-testid={`si-row-${si.id}`}>
                <td className="px-4 py-3"><div className="text-sm font-medium text-[#1f1f1f]">{si.client_name || '-'}</div><div className="text-xs text-gray-400">{si.name}</div></td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 bg-gray-50 capitalize">{si.instruction_type?.replace('_', ' ')}</span></td>
                <td className="px-4 py-3 text-xs text-gray-600"><div>{si.from_account_number || si.from_account_id?.substring(0, 8)}</div><div className="text-gray-400">to {si.to_account_number || si.to_account_id?.substring(0, 8)}</div></td>
                <td className="px-4 py-3 text-sm text-right font-mono font-medium">{fmt(si.amount)}</td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">{si.frequency} (day {si.day_of_month || 1})</td>
                <td className="px-4 py-3 text-xs text-gray-600">{si.next_execution_date || '-'}</td>
                <td className="px-4 py-3 text-center text-sm font-mono">{si.execution_count || 0}</td>
                <td className="px-4 py-3 text-center"><span className={`text-xs px-2 py-0.5 capitalize ${statusStyles[si.status] || 'bg-gray-100'}`}>{si.status?.replace('_', ' ')}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {si.status === 'pending_approval' && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleAction(si.id, 'approve')} className="text-emerald-500" title="Approve"><Check className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleAction(si.id, 'reject')} className="text-red-500" title="Reject"><X className="w-4 h-4" /></Button>
                      </>
                    )}
                    {si.status === 'active' && <Button variant="ghost" size="sm" onClick={() => handleAction(si.id, 'pause')} className="text-blue-500" title="Pause"><Pause className="w-4 h-4" /></Button>}
                    {si.status === 'paused' && <Button variant="ghost" size="sm" onClick={() => handleAction(si.id, 'resume')} className="text-emerald-500" title="Resume"><Play className="w-4 h-4" /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => viewHistory(si.id)} className="text-gray-500" title="History"><History className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(si.id)} className="text-red-400" title="Delete"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="si-create-desc">
          <DialogHeader><DialogTitle>Create Standing Instruction</DialogTitle></DialogHeader>
          <p id="si-create-desc" className="sr-only">Form to create a recurring instruction</p>
          <div className="space-y-4">
            {/* Client Search */}
            <div>
              <Label>Client *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <Input className="pl-9" value={clientSearch} onChange={e => searchClients(e.target.value)} placeholder="Search client by name or ID..." data-testid="si-client-search" />
              </div>
              {clientResults.length > 0 && (
                <div className="border border-gray-200 mt-1 max-h-32 overflow-y-auto">
                  {clientResults.map(c => (
                    <button key={c.id} onClick={() => selectClient(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-50">
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      <span className="text-xs text-gray-400 ml-2">{c.id_number || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div><Label>Instruction Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Monthly loan repayment" data-testid="si-name-input" /></div>

            <div><Label>Instruction Type *</Label>
              <Select value={form.instruction_type} onValueChange={v => setForm({...form, instruction_type: v, to_account_id: ''})}>
                <SelectTrigger data-testid="si-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Transfer (Savings to Savings)</SelectItem>
                  <SelectItem value="loan_repayment">Loan Repayment (Savings to Loan)</SelectItem>
                  <SelectItem value="sweep">Sweep (Move excess funds)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div><Label>Source Account (Debit) *</Label>
              <Select value={form.from_account_id} onValueChange={v => setForm({...form, from_account_id: v})}>
                <SelectTrigger data-testid="si-from"><SelectValue placeholder={clientAccounts.length ? 'Select account' : 'Search client first'} /></SelectTrigger>
                <SelectContent>
                  {clientAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.account_number} — {fmt(a.balance)} ({a.product_type || 'Savings'})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div><Label>Destination Account (Credit) *</Label>
              {form.instruction_type === 'loan_repayment' ? (
                <Select value={form.to_account_id} onValueChange={v => setForm({...form, to_account_id: v})}>
                  <SelectTrigger data-testid="si-to"><SelectValue placeholder={clientLoans.length ? 'Select loan' : 'No active loans'} /></SelectTrigger>
                  <SelectContent>
                    {clientLoans.map(l => <SelectItem key={l.id} value={l.id}>{l.loan_number} — {fmt(l.outstanding_balance)} outstanding</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={form.to_account_id} onValueChange={v => setForm({...form, to_account_id: v})}>
                  <SelectTrigger data-testid="si-to"><SelectValue placeholder={clientAccounts.length ? 'Select account' : 'Search client first'} /></SelectTrigger>
                  <SelectContent>
                    {clientAccounts.filter(a => a.id !== form.from_account_id).map(a => <SelectItem key={a.id} value={a.id}>{a.account_number} — {fmt(a.balance)} ({a.product_type || 'Savings'})</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amount (R) *</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="si-amount-input" /></div>
              <div><Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={v => setForm({...form, frequency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Day of Month</Label><Input type="number" min="1" max="28" value={form.day_of_month} onChange={e => setForm({...form, day_of_month: e.target.value})} /></div>
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">This instruction requires <strong>management approval</strong> before it becomes active (maker-checker).</p>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="si-submit-btn">Submit for Approval</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Execution History Modal */}
      <Dialog open={!!showHistory} onOpenChange={() => setShowHistory(null)}>
        <DialogContent className="max-w-md max-h-[70vh] overflow-y-auto" aria-describedby="hist-desc">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#C5A059]" />Execution History</DialogTitle></DialogHeader>
          <p id="hist-desc" className="sr-only">History of executions</p>
          {history.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No executions yet.</p> : (
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.id} className={`p-3 border text-sm ${h.status === 'success' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                  <div className="flex justify-between"><span className={`font-medium ${h.status === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>{h.status === 'success' ? 'Executed' : 'Failed'}</span><span className="text-xs text-gray-500">{h.executed_at?.substring(0, 10)}</span></div>
                  <p className="text-xs text-gray-600 mt-1">{fmt(h.amount)}</p>
                  {h.error && <p className="text-xs text-red-600 mt-1">{h.error}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default StandingInstructionsPage;
